import React from "react";
import { Link } from "react-router-dom";
import { SiteHeader } from "@/components/SiteHeader";
import { BlogSubscribeForm } from "@/components/BlogSubscribeForm";
import { usePageMeta } from "@/lib/use-page-meta";
import { blogPosts } from "@/lib/blog-posts";
import { ArrowRight, BookOpen } from "lucide-react";

export default function Blog() {
  usePageMeta({
    title: "Blog",
    description:
      "Plain-English guides on Google Analytics 4, GA4 reports, and turning website data into business decisions. Practical reads from the Plainly team.",
    path: "/blog",
  });

  // Sort newest first
  const posts = [...blogPosts].sort((a, b) => b.date.localeCompare(a.date));

  return (
    <div className="min-h-screen bg-[#F7F5F0] text-[#1E392A]">
      <SiteHeader />
      <main className="max-w-4xl mx-auto px-6 md:px-8 py-12 md:py-20">
        <div className="tag-label text-[#E27D60] mb-3">The Plainly blog</div>
        <h1 className="font-display text-4xl sm:text-5xl lg:text-6xl">Analytics, but readable.</h1>
        <p className="mt-4 text-lg text-[#1E392A]/75 max-w-2xl">
          Honest, plain-English guides for non-technical business owners trying to make sense of their website data.
        </p>

        <div className="mt-12 space-y-5">
          {posts.map((post) => (
            <Link
              key={post.slug}
              to={`/blog/${post.slug}`}
              className="block rounded-3xl bg-white border border-black/5 p-6 md:p-8 bento-shadow hover:border-[#E27D60]/40 transition-colors group"
              data-testid={`blog-post-card-${post.slug}`}
            >
              <div className="flex items-center gap-2 text-xs text-[#1E392A]/55">
                <BookOpen className="w-3.5 h-3.5" />
                <time dateTime={post.date}>{formatDate(post.date)}</time>
                <span>·</span>
                <span>{post.readMinutes} min read</span>
              </div>
              <h2 className="font-display text-2xl md:text-3xl mt-3 group-hover:text-[#E27D60] transition-colors">
                {post.title}
              </h2>
              <p className="mt-3 text-[#1E392A]/75 leading-relaxed">{post.description}</p>
              <div className="mt-4 inline-flex items-center gap-1.5 text-sm font-medium text-[#E27D60]">
                Read post <ArrowRight className="w-4 h-4" />
              </div>
            </Link>
          ))}
        </div>

        <div className="mt-16">
          <BlogSubscribeForm source="blog-index" />
        </div>
      </main>
    </div>
  );
}

function formatDate(iso) {
  try {
    return new Date(iso).toLocaleDateString("en-US", { year: "numeric", month: "long", day: "numeric" });
  } catch {
    return iso;
  }
}
